import {
  ErrorBoundary,
  Toaster as SonnerToaster,
  ToastToaster,
  TooltipProvider,
} from "@nfp-tracker/components";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { AuthChangeEvent } from "@supabase/supabase-js";
import { PropsWithChildren, useEffect, useRef } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "./integrations/supabase/client";
import SafeArea from "./components/SafeArea";
import { AppThemeProvider } from "./components/AppThemeProvider";
import { InstructorClientProvider } from "./components/InstructorClientProvider";
import { SubscriptionAccessGuard } from "./components/SubscriptionAccessGuard";
import {
  getIosCurrentEntitlements,
  isNativeIosPurchasesAvailable,
} from "./plugins/appStorePurchases";
import Auth from "./pages/Auth";
import SignupConfirmEmail from "./pages/SignupConfirmEmail";
import Calendar from "./pages/Calendar";
import CycleHistory from "./pages/CycleHistory";
import Dashboard from "./pages/Dashboard";
import GettingStarted from "./pages/GettingStarted";
import Help from "./pages/Help";
import InstructorSetup from "./pages/InstructorSetup";
import InstructorClients from "./pages/instructor/InstructorClients";
import InstructorClientChart from "./pages/instructor/InstructorClientChart";
import InviteAccept from "./pages/InviteAccept";
import NotFound from "./pages/NotFound";
import Onboarding from "./pages/Onboarding";
import Observations from "./pages/Observations";
import BulkEntry from "./pages/BulkEntry";
import Settings from "./pages/Settings";
import Subscription from "./pages/Subscription";
import AppShellLayout from "./layouts/AppShellLayout";
import { ROUTES } from "./constants/routes";
import { OwnerContextProvider } from "./components/OwnerContextProvider";
import { TrackingRouteGate } from "./components/TrackingRouteGate";
import { cancelDailyNotification, syncDailyReminderNotifications } from "./utils/pushNotifications";
import {
  ensureUserBaselineRows,
  loadAccountRole,
  needsOnboarding,
  resolveOwnerContext,
} from "./integrations/supabase/domain";
import { invokeEdgeFunction } from "./integrations/supabase/functions";
import {
  captureException,
  clearAnalyticsUser,
  identifyAnalyticsUser,
  setVerifiedAnalyticsIdentity,
  trackAppLifecycle,
  trackEvent,
  trackPageView,
} from "./integrations/analytics/client";
import { sanitizePath } from "./integrations/analytics/sanitize";
import { syncAppStoreTransactions } from "./utils/appleSubscriptionSync";
import { markSubscriptionBootstrapComplete } from "./utils/subscriptionBootstrap";
import { readUnverifiedTokenClaim } from "./utils/accessTokenClaims";
import { writeActorRoleHint } from "./utils/actorRoleHint";
import { useMinVersionGate } from "./hooks/useMinVersionGate";
import { UpdateRequiredScreen } from "./components/UpdateRequiredScreen";

const queryClient = new QueryClient();

const getErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== "object" || !("message" in error)) {
    return "";
  }

  const message = (error as { message?: unknown }).message;
  return typeof message === "string" ? message : "";
};

const isLikelyStaleAuthError = (error: unknown): boolean => {
  const message = getErrorMessage(error).toLowerCase();
  if (!message) {
    return false;
  }

  return [
    "user from sub claim in jwt does not exist",
    "auth session missing",
    "invalid refresh token",
    "refresh token not found",
    "jwt",
    "violates foreign key constraint",
  ].some((token) => message.includes(token));
};

const buildAnalyticsIdentityTraits = (user: {
  email?: string | null;
  created_at?: string | null;
  last_sign_in_at?: string | null;
  email_confirmed_at?: string | null;
  phone?: string | null;
  identities?: Array<{ provider?: string } | unknown> | null;
  app_metadata?: { provider?: unknown; providers?: unknown } | null;
  user_metadata?: {
    first_name?: unknown;
    last_name?: unknown;
    full_name?: unknown;
    name?: unknown;
  } | null;
}) => {
  const firstName =
    typeof user.user_metadata?.first_name === "string"
      ? user.user_metadata.first_name.trim()
      : "";
  const lastName =
    typeof user.user_metadata?.last_name === "string"
      ? user.user_metadata.last_name.trim()
      : "";
  const fullNameFromParts = `${firstName} ${lastName}`.trim();
  const metadataFullName =
    typeof user.user_metadata?.full_name === "string"
      ? user.user_metadata.full_name.trim()
      : typeof user.user_metadata?.name === "string"
        ? user.user_metadata.name.trim()
        : "";

  const authProviders = Array.isArray(user.app_metadata?.providers)
    ? user.app_metadata.providers.filter(
        (provider): provider is string => typeof provider === "string"
      )
    : [];
  const primaryProvider =
    typeof user.app_metadata?.provider === "string"
      ? user.app_metadata.provider
      : authProviders[0] ?? null;

  return {
    email: user.email ?? null,
    name: metadataFullName || fullNameFromParts || null,
    providerCount: user.identities?.length ?? 0,
    // PostHog's own "Created at" column on a person is not a filterable property, so the
    // signup date has to exist as one of ours for cohorts and date filters to see it.
    signup_date: user.created_at ?? null,
    auth_provider: primaryProvider,
    auth_providers: authProviders.length > 0 ? authProviders : null,
    verified_email: Boolean(user.email_confirmed_at),
    last_sign_in_at: user.last_sign_in_at ?? null,
    has_phone: Boolean(user.phone),
  };
};

// Only auth transitions that can actually change who the reminder belongs to, or whether
// it should exist at all. TOKEN_REFRESHED/INITIAL_SESSION fire routinely (and INITIAL_SESSION
// fires right alongside the mount-time sync below), so re-arming on them produced
// back-to-back duplicate notification_reminder_synced events for the same outcome.
const REMINDER_RESYNC_AUTH_EVENTS = new Set<AuthChangeEvent>([
  "SIGNED_IN",
  "SIGNED_OUT",
  "USER_UPDATED",
]);

const useNotificationBootstrap = () => {
  useEffect(() => {
    let isUnmounted = false;
    // The event exists to correlate rearm timestamps against releases, so one event per
    // distinct outcome is all we need — repeats of an unchanged outcome are pure noise.
    let lastTrackedSyncSignature: string | null = null;
    let syncInFlight = false;

    const syncNotificationsForCurrentUser = async () => {
      if (syncInFlight) {
        return;
      }

      syncInFlight = true;
      try {
        await runNotificationSync();
      } finally {
        syncInFlight = false;
      }
    };

    const runNotificationSync = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user?.id) {
        lastTrackedSyncSignature = null;
        await cancelDailyNotification();
        return;
      }

      const { data: profile, error } = await supabase
        .from("user_accounts")
        .select("notification_enabled, notification_time, push_notifications_enabled")
        .eq("user_id", session.user.id)
        .maybeSingle();

      if (error || isUnmounted) {
        return;
      }

      try {
        const syncResult = await syncDailyReminderNotifications(
          {
            notificationsEnabled: profile?.notification_enabled ?? true,
            notificationTime: profile?.notification_time,
            pushNotificationsEnabled: profile?.push_notifications_enabled ?? false,
          },
          {
            allowPermissionPrompt: false,
            requestPushRegistration: false,
          }
        );

        const syncSignature = [
          session.user.id,
          syncResult.scheduled,
          syncResult.permissionGranted ?? null,
        ].join(":");

        if (!isUnmounted && syncSignature !== lastTrackedSyncSignature) {
          lastTrackedSyncSignature = syncSignature;
          // Lets us correlate rearm timestamps against release timestamps to confirm
          // whether app updates are the ones interrupting the daily reminder schedule.
          trackEvent("notification_reminder_synced", {
            scheduled: syncResult.scheduled,
            permissionGranted: syncResult.permissionGranted ?? null,
          });
        }
      } catch (syncError) {
        // This is the only place the daily reminder gets (re)armed on app open — a
        // silently swallowed failure here means the reminder just stops firing with
        // no signal to us, so report it instead of letting the rejection vanish.
        if (!isUnmounted) {
          console.error("[NotificationBootstrap] Failed syncing daily reminder", syncError);
          trackEvent("notification_bootstrap_failed", {
            phase: "sync",
          });
          captureException(syncError, {
            source: "notification_bootstrap",
            phase: "sync",
          });
        }
      }
    };

    void syncNotificationsForCurrentUser();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (!REMINDER_RESYNC_AUTH_EVENTS.has(event)) {
        return;
      }

      void syncNotificationsForCurrentUser();
    });

    return () => {
      isUnmounted = true;
      subscription.unsubscribe();
    };
  }, []);
};

const useAnalyticsIdentityBootstrap = () => {
  useEffect(() => {
    let isUnmounted = false;
    // The signature is derived from the user id and doesn't change, so one round trip per
    // user per session is enough — auth events like TOKEN_REFRESHED shouldn't re-fetch it.
    let verifiedUserId: string | null = null;

    /**
     * The signature comes down as a custom claim on the access token (see the
     * custom_access_token_hook migration), so this is a local read — no request, and it
     * refreshes with the session for free. Absent claim means the environment has no signing
     * secret configured, which leaves support tickets on PostHog's anonymous per-browser
     * scoping and is otherwise harmless.
     */
    const verifyAnalyticsIdentity = (userId: string, accessToken: string | undefined) => {
      if (verifiedUserId === userId) {
        return;
      }

      const hash = readUnverifiedTokenClaim(accessToken, "posthog_identity_hash");
      if (!hash) {
        return;
      }

      verifiedUserId = userId;
      setVerifiedAnalyticsIdentity(userId, hash);
    };

    const syncAnalyticsIdentity = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (isUnmounted) {
        return;
      }

      if (!session?.user?.id) {
        verifiedUserId = null;
        clearAnalyticsUser();
        return;
      }

      identifyAnalyticsUser(
        session.user.id,
        buildAnalyticsIdentityTraits(session.user)
      );
      verifyAnalyticsIdentity(session.user.id, session.access_token);
    };

    void syncAnalyticsIdentity();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user?.id) {
        verifiedUserId = null;
        clearAnalyticsUser();
        return;
      }

      identifyAnalyticsUser(
        session.user.id,
        buildAnalyticsIdentityTraits(session.user)
      );
      verifyAnalyticsIdentity(session.user.id, session.access_token);
    });

    return () => {
      isUnmounted = true;
      subscription.unsubscribe();
    };
  }, []);
};

const useNativeAnalyticsLifecycle = () => {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let isUnmounted = false;
    let listenerHandle: { remove: () => Promise<void> } | null = null;
    let lastIsActive: boolean | null = null;

    const register = async () => {
      const initialState = await CapacitorApp.getState();
      if (isUnmounted) {
        return;
      }

      lastIsActive = initialState.isActive;
      listenerHandle = await CapacitorApp.addListener("appStateChange", ({ isActive }) => {
        if (lastIsActive === isActive) {
          return;
        }

        lastIsActive = isActive;
        void trackAppLifecycle(isActive);
      });
    };

    void register();

    return () => {
      isUnmounted = true;
      if (listenerHandle) {
        void listenerHandle.remove();
      }
    };
  }, []);
};

const useSubscriptionBootstrap = () => {
  useEffect(() => {
    let isUnmounted = false;
    const ensuredOwnerIds = new Set<string>();
    const inFlightOwnerIds = new Set<string>();

    const ensureSignupSubscriptionForCurrentOwner = async () => {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        console.error("[SubscriptionBootstrap] Session fetch failed, clearing local auth", sessionError);
        trackEvent("subscription_bootstrap_failed", {
          phase: "getSession",
        });
        captureException(sessionError, {
          source: "subscription_bootstrap",
          phase: "getSession",
        });
        const { error: signOutError } = await supabase.auth.signOut({ scope: "local" });
        if (signOutError) {
          console.error("[SubscriptionBootstrap] Failed local sign-out after session error", signOutError);
        }
        return;
      }

      if (!session?.user?.id) {
        return;
      }

      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      const actorUserId = user?.id;
      if (!actorUserId) {
        if (userError) {
          console.error("[SubscriptionBootstrap] User fetch failed, clearing local auth", userError);
          trackEvent("subscription_bootstrap_failed", {
            phase: "getUser",
          });
          captureException(userError, {
            source: "subscription_bootstrap",
            phase: "getUser",
          });
          const { error: signOutError } = await supabase.auth.signOut({ scope: "local" });
          if (signOutError) {
            console.error("[SubscriptionBootstrap] Failed local sign-out after user error", signOutError);
          }
        }
        return;
      }

      try {
        const ownerCtx = await resolveOwnerContext(actorUserId);
        const ownerUserId = ownerCtx.ownerUserId;

        if (
          !ownerUserId
          || ensuredOwnerIds.has(ownerUserId)
          || inFlightOwnerIds.has(ownerUserId)
        ) {
          return;
        }

        inFlightOwnerIds.add(ownerUserId);

        try {
          if (isNativeIosPurchasesAvailable()) {
            try {
              const entitlements = await getIosCurrentEntitlements();
              const matchingEntitlements = entitlements.filter((transaction) =>
                !transaction.appAccountToken || transaction.appAccountToken === ownerUserId
              );

              if (matchingEntitlements.length) {
                const syncResult = await syncAppStoreTransactions(matchingEntitlements, ownerUserId);
                if (syncResult.billingTransitionWarning) {
                  console.warn("[SubscriptionBootstrap] Apple sync completed with warning", {
                    ownerUserId,
                    warning: syncResult.billingTransitionWarning,
                  });
                }
              }
            } catch (error) {
              if (!isUnmounted) {
                console.error("[SubscriptionBootstrap] Failed syncing Apple entitlements", error);
                trackEvent("subscription_bootstrap_failed", {
                  phase: "sync_apple",
                });
                captureException(error, {
                  source: "subscription_bootstrap",
                  phase: "sync_apple",
                });
              }
            }
          }

          const { data: subscriptionRow, error: subscriptionError } = await supabase
            .from("user_subscriptions")
            .select("stripe_customer_id, stripe_price_id, stripe_product_id, stripe_subscription_id, subscription_status, trial_end_date")
            .eq("owner_user_id", ownerUserId)
            .maybeSingle();

          if (subscriptionError) {
            throw subscriptionError;
          }

          const hasConfiguredSubscription = Boolean(
            subscriptionRow?.stripe_customer_id
            || subscriptionRow?.stripe_price_id
            || subscriptionRow?.stripe_product_id
            || subscriptionRow?.stripe_subscription_id
          );
          if (
            subscriptionRow?.stripe_customer_id
            && subscriptionRow?.stripe_subscription_id
            && !subscriptionRow?.stripe_price_id
            && !subscriptionRow?.stripe_product_id
          ) {
            const { error: clearStaleIdsError } = await supabase
              .from("user_subscriptions")
              .update({
                stripe_customer_id: null,
                stripe_subscription_id: null,
              })
              .eq("owner_user_id", ownerUserId);

            if (clearStaleIdsError) {
              throw clearStaleIdsError;
            }
          }

          // create-signup-subscription can only create for the authenticated owner account.
          if (ownerUserId !== actorUserId) {
            if (!hasConfiguredSubscription) {
              console.info("[SubscriptionBootstrap] Missing owner subscription; waiting for owner sign-in", {
                actorUserId,
                ownerUserId,
              });
            }
            return;
          }

          // Only ensure a signup subscription when the user has no recorded subscription
          // state at all — calling this for anyone with a known status (even inactive/
          // canceled) risks the edge function reconciling against the wrong Stripe
          // customer/subscription and clobbering a manually-configured one.
          const shouldEnsureSignupSubscription = !subscriptionRow?.subscription_status;

          if (!shouldEnsureSignupSubscription) {
            ensuredOwnerIds.add(ownerUserId);
            return;
          }

          const { data, error } = await invokeEdgeFunction("create-signup-subscription");
          if (error) {
            throw error;
          }

          ensuredOwnerIds.add(ownerUserId);

          if (data?.skipped) {
            console.info("[SubscriptionBootstrap] Signup subscription call skipped", {
              ownerUserId,
              reason: data.reason ?? null,
            });
          }
        } finally {
          inFlightOwnerIds.delete(ownerUserId);
          markSubscriptionBootstrapComplete(ownerUserId);
        }
      } catch (error) {
        if (!isUnmounted) {
          console.error("[SubscriptionBootstrap] Failed ensuring startup subscription", error);
          trackEvent("subscription_bootstrap_failed", {
            phase: "ensure",
          });
          captureException(error, {
            source: "subscription_bootstrap",
            phase: "ensure",
          });
        }
      }
    };

    void ensureSignupSubscriptionForCurrentOwner();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (nextSession?.user?.id) {
        void ensureSignupSubscriptionForCurrentOwner();
      }
    });

    return () => {
      isUnmounted = true;
      subscription.unsubscribe();
    };
  }, []);
};

const RootRedirect = () => {
  const navigate = useNavigate();

  useEffect(() => {
    let isUnmounted = false;

    const clearLocalAuthAndRouteToLogin = async (reason: string, error?: unknown) => {
      console.warn("[RootRedirect] Clearing stale local auth session", {
        reason,
        error,
      });

      const { error: signOutError } = await supabase.auth.signOut({ scope: "local" });
      if (signOutError) {
        console.error("[RootRedirect] Failed local sign-out while recovering auth state", signOutError);
      }

      if (!isUnmounted) {
        navigate(ROUTES.login, { replace: true });
      }
    };

    const routeUser = async () => {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        await clearLocalAuthAndRouteToLogin("getSession_error", sessionError);
        return;
      }

      if (!session) {
        if (!isUnmounted) {
          navigate(ROUTES.login, { replace: true });
        }
        return;
      }

      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        await clearLocalAuthAndRouteToLogin("getUser_invalid", userError ?? new Error("Missing user"));
        return;
      }

      try {
        await ensureUserBaselineRows({
          userId: user.id,
          email: user.email,
          firstName: (user.user_metadata?.first_name as string | undefined) || null,
          lastName: (user.user_metadata?.last_name as string | undefined) || null,
        });

        const mustOnboard = await needsOnboarding(user.id);
        if (mustOnboard) {
          if (!isUnmounted) {
            navigate(ROUTES.onboarding, { replace: true });
          }
          return;
        }

        // Instructor accounts have no tracking data of their own; their home is the roster.
        const role = await loadAccountRole(user.id);
        if (role) {
          writeActorRoleHint({ userId: user.id, role });
        }
        if (!isUnmounted) {
          navigate(role === "instructor" ? ROUTES.instructorClients : ROUTES.observations, {
            replace: true,
          });
        }
      } catch (error) {
        if (isLikelyStaleAuthError(error)) {
          await clearLocalAuthAndRouteToLogin("baseline_or_onboarding_stale_auth", error);
          return;
        }

        console.error("[RootRedirect] Failed to route authenticated user", error);
        captureException(error, {
          source: "root_redirect",
          phase: "route_authenticated_user",
        });
        if (!isUnmounted) {
          navigate(ROUTES.login, { replace: true });
        }
      }
    };

    void routeUser();
    return () => {
      isUnmounted = true;
    };
  }, [navigate]);

  return null;
};

const CapacitorDeepLinkHandler = () => {
  const navigate = useNavigate();
  const lastHandledUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const toInAppPath = (rawUrl: string) => {
      try {
        const parsed = new URL(rawUrl);
        if (parsed.protocol === "http:" || parsed.protocol === "https:") {
          return `${parsed.pathname}${parsed.search}${parsed.hash}`;
        }

        const hostPrefix = parsed.hostname ? `/${parsed.hostname}` : "";
        return `${hostPrefix}${parsed.pathname}${parsed.search}${parsed.hash}`;
      } catch {
        return null;
      }
    };

    // Console output is forwarded to PostHog Logs, so URLs are redacted (invite tokens, ids).
    const logDeepLink = (event: string, payload?: Record<string, unknown>) => {
      const safePayload = Object.fromEntries(
        Object.entries(payload || {}).map(([key, value]) => [
          key,
          typeof value === "string" ? sanitizePath(value) : value,
        ])
      );
      console.info("[DeepLink]", event, safePayload);
    };

    const handleIncomingUrl = (rawUrl: string | null | undefined) => {
      logDeepLink("incoming", { rawUrl });

      if (!rawUrl || rawUrl === lastHandledUrlRef.current) {
        logDeepLink("skip", {
          reason: !rawUrl ? "empty-url" : "duplicate-url",
          rawUrl,
        });
        return;
      }

      const targetPath = toInAppPath(rawUrl);
      if (!targetPath) {
        logDeepLink("skip", { reason: "unparseable-url", rawUrl });
        return;
      }

      const inviteMatch = targetPath.match(/^\/invite\/([^/?#]+)/i);
      logDeepLink("parsed", {
        rawUrl,
        targetPath,
        routeType: inviteMatch ? "invite" : "other",
        hasInviteToken: Boolean(inviteMatch?.[1]),
      });

      lastHandledUrlRef.current = rawUrl;
      trackEvent("deep_link_opened", {
        path: sanitizePath(targetPath),
        routeType: inviteMatch ? "invite" : "other",
        source: "native",
      });
      navigate(targetPath, { replace: true });
      logDeepLink("navigated", { targetPath });
    };

    let isUnmounted = false;
    let listenerHandle: { remove: () => Promise<void> } | null = null;

    const register = async () => {
      listenerHandle = await CapacitorApp.addListener("appUrlOpen", ({ url }) => {
        logDeepLink("appUrlOpen", { url });
        handleIncomingUrl(url);
      });

      logDeepLink("listener-registered");
      const launchUrl = await CapacitorApp.getLaunchUrl();
      if (!isUnmounted) {
        logDeepLink("launch-url", { url: launchUrl?.url || null });
        handleIncomingUrl(launchUrl?.url);
      }
    };

    void register();

    return () => {
      isUnmounted = true;
      logDeepLink("listener-cleanup");
      if (listenerHandle) {
        void listenerHandle.remove();
      }
    };
  }, [navigate]);

  return null;
};

const AnalyticsRouteTracker = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);

  return null;
};

const App = () => (
  <AppWithBootstrap />
);

const SafeAreaRouteWrapper = ({ children }: PropsWithChildren) => {
  const { pathname } = useLocation();
  const keepBottomSafeAreaInset = [
    ROUTES.auth,
    ROUTES.login,
    ROUTES.signup,
    ROUTES.signupConfirmEmail,
    ROUTES.onboarding,
    ROUTES.invite,
  ].some((route) => pathname.startsWith(route));

  return (
    <SafeArea
      contentClassName={!keepBottomSafeAreaInset ? "safe-area__content--inflow-no-bottom-inset" : undefined}
    >
      {children}
    </SafeArea>
  );
};

const MinVersionGate = ({ children }: PropsWithChildren) => {
  const { isBlocked, updateMessage, storeUrl } = useMinVersionGate();

  if (isBlocked) {
    return <UpdateRequiredScreen updateMessage={updateMessage} storeUrl={storeUrl} />;
  }

  return <>{children}</>;
};

const AppWithBootstrap = () => {
  useNotificationBootstrap();
  useSubscriptionBootstrap();
  useAnalyticsIdentityBootstrap();
  useNativeAnalyticsLifecycle();

  return (
    <ErrorBoundary
      onError={(error, info) => {
        captureException(error, {
          source: "error_boundary",
          componentStack: info.componentStack,
        });
      }}
    >
      <AppThemeProvider>
        <QueryClientProvider client={queryClient}>
          <MinVersionGate>
            <TooltipProvider>
              <ToastToaster />
              <SonnerToaster />
              <BrowserRouter>
                {/* Inside the router on purpose: the active client is derived from the
                    `/instructor/clients/:clientUserId` URL, so the provider needs the location. */}
                <InstructorClientProvider>
                {/* Owner/actor context resolved once per signed-in user (and re-keyed on every
                    auth change) for the shell, nav, route gates and pages. Sits under the client
                    provider because the client-view layer is keyed on the URL's client id. */}
                <OwnerContextProvider>
                <SafeAreaRouteWrapper>
                  <AnalyticsRouteTracker />
                  <CapacitorDeepLinkHandler />
                  <Routes>
                    <Route path={ROUTES.root} element={<RootRedirect />} />
                    <Route path={ROUTES.auth} element={<Auth mode="login" />} />
                    <Route path={ROUTES.login} element={<Auth mode="login" />} />
                    <Route path={ROUTES.signup} element={<Auth mode="signup" />} />
                    <Route path={ROUTES.signupConfirmEmail} element={<SignupConfirmEmail />} />
                    <Route path={ROUTES.onboarding} element={<Onboarding />} />
                    <Route path={ROUTES.instructorSetup} element={<InstructorSetup />} />
                    <Route path={ROUTES.invite} element={<InviteAccept />} />
                    <Route element={<AppShellLayout />}>
                      {/* Personal tracking pages: instructors have none of their own, so the gate
                          sends them to the roster before any tracking content renders. */}
                      <Route path={ROUTES.gettingStarted} element={<TrackingRouteGate><GettingStarted /></TrackingRouteGate>} />
                      <Route path={ROUTES.observations} element={<TrackingRouteGate><Observations /></TrackingRouteGate>} />
                      <Route path={ROUTES.bulkEntry} element={<TrackingRouteGate><BulkEntry /></TrackingRouteGate>} />
                      <Route path={ROUTES.legacyToday} element={<TrackingRouteGate><Observations /></TrackingRouteGate>} />
                      <Route path={ROUTES.calendar} element={<TrackingRouteGate><Calendar /></TrackingRouteGate>} />
                      <Route path={ROUTES.dashboard} element={<TrackingRouteGate><Dashboard /></TrackingRouteGate>} />
                      <Route path={ROUTES.cycleHistory} element={<TrackingRouteGate><CycleHistory /></TrackingRouteGate>} />
                      <Route path={ROUTES.settings} element={<Settings />} />
                      <Route
                        path={ROUTES.subscription}
                        element={
                          // Instructor accounts aren't billed; the gate sends them to the roster.
                          <TrackingRouteGate>
                            <SubscriptionAccessGuard>
                              <Subscription />
                            </SubscriptionAccessGuard>
                          </TrackingRouteGate>
                        }
                      />
                      <Route path={ROUTES.help} element={<Help />} />
                      {/* Instructor routes share the shell: the roster is their home, and a
                          client's chart is the ordinary Calendar/Insights/History pages rendered
                          against that client, with the ClientViewBanner naming whose data it is. */}
                      <Route path={ROUTES.instructorClients} element={<InstructorClients />} />
                      <Route
                        path={ROUTES.instructorClient}
                        element={<Navigate to="calendar" replace />}
                      />
                      <Route
                        path={ROUTES.instructorClientCalendar}
                        element={<InstructorClientChart tab="calendar" />}
                      />
                      <Route
                        path={ROUTES.instructorClientInsights}
                        element={<InstructorClientChart tab="insights" />}
                      />
                      <Route
                        path={ROUTES.instructorClientHistory}
                        element={<InstructorClientChart tab="history" />}
                      />
                    </Route>
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </SafeAreaRouteWrapper>
                </OwnerContextProvider>
                </InstructorClientProvider>
              </BrowserRouter>
            </TooltipProvider>
          </MinVersionGate>
        </QueryClientProvider>
      </AppThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
